import { useState } from "react"
import { Link } from "@tanstack/react-router"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useApiQuery } from "@/api/ApiQueryServiceProvider"
import { Search, Edit, Trash2, Box, ChevronLeft, ChevronRight, Loader2, Plus } from "lucide-react"
import { DeleteDeviceTypeComposer } from "@/forms/sensory"
import { useToast } from "@/contexts/ToastContext"

type DeviceTypeStatus = "research" | "testing" | "certified"

export interface DeviceType {
  id: string
  deviceTypeNumber: number
  deviceTypeName: string
  deviceTypeManufacturerNumber: number
  deviceTypeModel: string
  deviceTypeStatus: DeviceTypeStatus
  deviceTypeCategory: string
  manufacturerName?: string
}

const statusConfig: Record<DeviceTypeStatus, { className: string; label: string }> = {
  research: { className: "bg-muted text-muted-foreground", label: "Research" },
  testing: { className: "bg-secondary/15 text-secondary", label: "Testing" },
  certified: { className: "bg-primary/15 text-primary", label: "Certified" },
}

interface DeviceTypeListProps {
  onAdd?: () => void
  onEdit?: (deviceType: DeviceType) => void
}

export function DeviceTypeList({ onAdd, onEdit }: DeviceTypeListProps) {
  const { toast } = useToast()
  const [searchQuery, setSearchQuery] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedDeviceType, setSelectedDeviceType] = useState<DeviceType | null>(null)
  const itemsPerPage = 10

  // Fetch device types from API
  const { data, isLoading, isError, refetch } = useApiQuery(
    "getDeviceTypes",
    {},
    {}
  )

  // Map API response to component state, excluding archived items
  const deviceTypes: DeviceType[] = (data?.content?.deviceTypes || [])
    .filter((dt: any) => !dt.isArchived)
    .map((dt: any) => ({
      id: dt.id || String(dt.deviceTypeNumber),
      deviceTypeNumber: dt.deviceTypeNumber,
      deviceTypeName: dt.deviceTypeName,
      deviceTypeManufacturerNumber: dt.deviceTypeManufacturerNumber,
      deviceTypeModel: dt.deviceTypeModel,
      deviceTypeStatus: dt.deviceTypeStatus as DeviceTypeStatus,
      deviceTypeCategory: dt.deviceTypeCategory,
      manufacturerName: dt.manufacturerName,
    }))

  const filteredTypes = deviceTypes.filter(
    (type) =>
      type.deviceTypeName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      type.deviceTypeModel?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const totalPages = Math.ceil(filteredTypes.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedTypes = filteredTypes.slice(startIndex, startIndex + itemsPerPage)

  const handleDelete = (deviceType: DeviceType) => {
    setSelectedDeviceType(deviceType)
    setIsDeleteDialogOpen(true)
  }

  if (isLoading) {
    return (
      <Card className="shadow-sm">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    )
  }

  if (isError) {
    return (
      <Card className="shadow-sm">
        <CardContent className="flex flex-col items-center justify-center py-12 gap-4">
          <p className="text-destructive">Failed to load device types</p>
          <Button variant="outline" onClick={() => refetch()}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <Card className="shadow-sm pb-0">
        <CardHeader className="border-b bg-slate-700 text-white [&_p]:text-slate-300">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Box className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-xl">Device Types</CardTitle>
                <CardDescription className="text-base">Manage device type definitions</CardDescription>
              </div>
            </div>

            <Button size="sm" className="gap-2" onClick={() => onAdd?.()}>
              <Plus className="h-4 w-4" />
              Add Device Type
            </Button>
          </div>
        </CardHeader>
      </Card>

      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search device types..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  setCurrentPage(1)
                }}
                className="pl-9"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">
        {/* Table */}
        <Table className="text-base">
          <TableHeader>
            <TableRow className="bg-muted/30 hover:bg-muted/30">
              <TableHead className="w-[80px] font-semibold text-base">ID</TableHead>
              <TableHead className="font-semibold text-base">Name</TableHead>
              <TableHead className="font-semibold text-base">Model</TableHead>
              <TableHead className="font-semibold text-base">Category</TableHead>
              <TableHead className="font-semibold text-base">Status</TableHead>
              <TableHead className="w-[100px] text-right font-semibold text-base">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedTypes.length > 0 ? (
              paginatedTypes.map((type) => {
                const status = statusConfig[type.deviceTypeStatus] || statusConfig.research
                return (
                  <TableRow key={type.id} className="transition-colors hover:bg-muted/50">
                    <TableCell className="text-base text-muted-foreground">{type.deviceTypeNumber}</TableCell>
                    <TableCell className="font-medium text-base">
                      <Link
                        to="/sensory/device-types/$deviceTypeNumber"
                        params={{ deviceTypeNumber: String(type.deviceTypeNumber) }}
                        className="text-primary hover:underline"
                      >
                        {type.deviceTypeName}
                      </Link>
                    </TableCell>
                    <TableCell className="font-mono text-base text-muted-foreground">{type.deviceTypeModel}</TableCell>
                    <TableCell className="text-muted-foreground">{type.deviceTypeCategory || '—'}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={status.className}>
                        {status.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button asChild variant="ghost" size="icon" className="h-8 w-8">
                          <Link
                            to="/sensory/device-types/$deviceTypeNumber/edit"
                            params={{ deviceTypeNumber: String(type.deviceTypeNumber) }}
                          >
                            <Edit className="h-4 w-4" />
                            <span className="sr-only">Edit {type.deviceTypeName}</span>
                          </Link>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => handleDelete(type)}
                        >
                          <Trash2 className="h-4 w-4" />
                          <span className="sr-only">Delete {type.deviceTypeName}</span>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })
            ) : (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                  No device types found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t py-3">
            <p className="text-base text-muted-foreground">
              Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredTypes.length)} of{" "}
              {filteredTypes.length} types
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-base font-medium">Page {currentPage} of {totalPages}</span>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Device Type</DialogTitle>
            <DialogDescription>
              Please confirm you want to delete this device type.
            </DialogDescription>
          </DialogHeader>
          {selectedDeviceType && (
            <DeleteDeviceTypeComposer
              deviceTypeNumber={selectedDeviceType.deviceTypeNumber}
              deviceTypeName={selectedDeviceType.deviceTypeName}
              showHeader={false}
              onSuccess={() => {
                setIsDeleteDialogOpen(false)
                setSelectedDeviceType(null)
                refetch()
              }}
              onError={(error) => {
                toast({ title: 'Error', description: `Failed to delete device type: ${error.message}`, type: 'error' })
              }}
              onCancel={() => {
                setIsDeleteDialogOpen(false)
                setSelectedDeviceType(null)
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
